/**
 * Confirm Modal
 * A simple confirmation modal for delete and other actions
 */

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './ConfirmModal.css';

export interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'primary' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  confirmVariant = 'primary',
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const { t } = useTranslation('common');
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Focus confirm button after modal opens
      setTimeout(() => {
        confirmBtnRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return (
    <div className="confirm-modal-overlay" onClick={onCancel}>
      <div
        className="confirm-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
      >
        <div className="confirm-modal-header">
          <h3 id="confirm-modal-title">{title}</h3>
          <button
            className="confirm-modal-close"
            onClick={onCancel}
            aria-label={t('close')}
          >
            ×
          </button>
        </div>

        <div className="confirm-modal-body">
          <p>{message}</p>
        </div>

        <div className="confirm-modal-footer">
          <button
            className="confirm-modal-btn confirm-modal-btn--cancel"
            onClick={onCancel}
            disabled={loading}
          >
            {cancelText || t('cancel')}
          </button>
          <button
            ref={confirmBtnRef}
            className={`confirm-modal-btn confirm-modal-btn--${confirmVariant}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? t('processing') : (confirmText || t('confirm'))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
